import common from './common';
import EditorScene from './scenes/EditorScene';
import GameScene from './scenes/GameScene';
import LoadingScene from './scenes/LoadingScene';
import SelectScenarioScene from './scenes/SelectScenarioScene';
import SplashScene from './scenes/SplashScene';
import DevelopmentScene from './scenes/gui/DevelopmentScene';
import IntroScene from './scenes/gui/IntroScene';
import NewsScene from './scenes/gui/NewsScene';
import ReportScene from './scenes/gui/ReportScene';
import SettingsScene from './scenes/gui/SettingsScene';

export default class Application {
	init() {
		const query = window.location.search;

		if (query && process.env.NODE_ENV === 'development') {
			const params = new URLSearchParams(query);
			const editor = params.get('editor');
			const report = params.get('report');

			if (editor) {
				common.editor = true;
				this.showLoadingScene('editor');

				return;
			}

			// Debug-only: jump straight to report screen for tsunami win testing
			// e.g. /dev?report=tsunami_win
			if (report === 'tsunami_win') {
				common.selectedLevel = 'tsunami';
				common.difficulty = common.difficulty || 'easy';
				common.achievements = common.achievements || { unlocked: [] };
				common.score = 12345;
				common.scenario = {
					difficulty: common.difficulty,
					win: true,
					type: 'ts',
					targets: { housing: 50, hospital: 1, school: 1, mission: 1 },
					targetProgress: { housing: 50, hospital: 1, school: 1, mission: 1 },
					limits: { deaths: 100, damage: 100000 },
					budget: { remaining: 1000 },
					population: { housed: 50, sheltered: 40 },
					disaster: {
						totalDestroyed: 0,
						totalCost: 10000,
						totalDeaths: 0,
						totalInjury: 0,
						schoolDestroyed: false,
						hospitalDestroyed: false,
					},
					stopSounds: () => {},
				};

				this.showReportScene();

				return;
			}
		}

		// Deep link directly into a scenario (e.g. ?scenario=tsunami&difficulty=medium)
		if (query) {
			const params = new URLSearchParams(query);
			const scenarioParam = params.get('scenario');

			if (scenarioParam && common.config && Array.isArray(common.config.scenarios)) {
				const scenarios = common.config.scenarios;
				let scenarioIndex = -1;

				const numeric = parseInt(scenarioParam, 10);

				if (!Number.isNaN(numeric) && numeric >= 1 && numeric <= scenarios.length) {
					scenarioIndex = numeric - 1;
				} else {
					const target = scenarioParam.toLowerCase();

					scenarioIndex = scenarios.findIndex((s) => (s && s.toLowerCase && s.toLowerCase()) === target);
				}

				if (scenarioIndex !== -1) {
					common.selectedScenario = scenarioIndex + 1;
					common.selectedLevel = scenarios[scenarioIndex];

					const diffParam = params.get('difficulty');

					if (diffParam) {
						const diff = diffParam.toLowerCase();

						if (diff === 'easy' || diff === 'medium' || diff === 'hard') {
							common.difficulty = diff;
						}
					}

					// Optional: tracking for deep link selection
					try {
						common.tracking && common.tracking.track(new P3.TrackingDataGtagEvent('select_level', {
							event_category: 'UI',
							event_type: `select_level_${common.difficulty}`,
							page_container: common.selectedLevel,
						}));
					} catch (e) { /* no-op */ }

					this.showLoadingScene('game');

					return;
				}
			}
		}

		if (!common.debug) {
			window.addEventListener('beforeunload', (e) => {
				// console.log('beforeunload',window.unLoadType)
				if (window.unLoadType === 'Language') {
					// Language has already prompted and warned user of unload
					window.location.href = `${window.location.href}?lang=reloaded`;
				} else {
					e.preventDefault();
					e.returnValue = true;
				}
			});
		}

		this.showSplashScene();
	}

	showSelectScene() {
		const scene = new SelectScenarioScene();

		scene.autoAppear = false;
		scene.alwaysUpdate = true;

		scene.signals.next.add(() => {
			this.showLoadingScene('game');
		});
		scene.signals.previous.add(() => {
			this.showSplashScene();
		});

		this.fadeTransition(() => {
			P3.scene.show(scene);
		}, () => {
			scene.appear();
		}, this);

		return scene;
	}

	showLoadingScene(type) {
		const scene = new LoadingScene();

		scene.signals.next.add(() => {
			switch (type) {
			default:
			case 'game':
				this.showGameScene();
				break;
			case 'editor':
				this.showEditorScene();
				break;
			}
		});

		P3.scene.add(scene);

		return scene;
	}

	showSplashScene() {
		const scene = new SplashScene();

		scene.autoAppear = false;
		// scene.alwaysUpdate = true;

		scene.signals.next.add(() => {
			this.showSelectScene();
		});
		scene.signals.pause.add(() => {
			this.showSettingsScene('splash');
		});

		this.fadeTransition(() => {
			P3.scene.show(scene);
		}, () => {
			scene.appear();
		}, this);

		return scene;
	}

	showGameScene() {
		const scene = new GameScene();

		scene.autoAppear = false;
		scene.alwaysUpdate = true;

		scene.signals.next.add(() => {
			this.showNewsScene();
		});
		scene.signals.previous.add(() => {
			this.showSplashScene();
		});
		scene.signals.report.add(() => {
			this.showReportScene();
		});
		scene.signals.intro.add(() => {
			this.showIntroScene();
		});
		scene.signals.pause.add(() => {
			this.showSettingsScene('game');
		});
		scene.signals.develop.add((scene, tile, type) => {
			common.paused = true;

			const develop = this.showDevelopmentScene(tile, type);

			develop.signals.next.add((s, tile, id, cost) => {
				if (type === 3) {
					scene.upgradeBuilding(tile, id, cost);
				} else {
					scene.buildStructure(tile, id, cost);
				}
			}, null, 1);
		});

		this.fadeTransition(() => {
			P3.scene.show(scene);
		}, () => {
			scene.appear();
		}, this);

		return scene;
	}

	showEditorScene() {
		const scene = new EditorScene();

		scene.autoAppear = false;

		this.fadeTransition(() => {
			P3.scene.show(scene);
		}, () => {
			scene.appear();
		}, this);

		return scene;
	}

	showDevelopmentScene(tile, type) {
		const scene = new DevelopmentScene(tile, type);

		scene.signals.next.add(() => {
			P3.scene.remove();
			common.paused = false;
		});
		scene.signals.previous.add(() => {
			P3.scene.remove();
			common.paused = false;
		});

		P3.scene.add(scene);

		return scene;
	}

	showIntroScene() {
		const scene = new IntroScene();

		scene.signals.next.add(() => {
			P3.scene.remove();
		});
		scene.signals.previous.add(() => {
			P3.scene.remove();
		});

		P3.scene.add(scene);

		return scene;
	}

	showNewsScene() {
		const scene = new NewsScene();

		scene.signals.next.add(() => {
			this.showReportScene();
		});
		scene.signals.previous.add(() => {
			P3.scene.remove();
		});

		P3.scene.add(scene);

		return scene;
	}

	showReportScene() {
		const scene = new ReportScene();

		scene.signals.next.add(() => {
			P3.scene.remove();
			this.showGameScene();
		});
		scene.signals.previous.add(() => {
			this.showSplashScene();
		});

		P3.scene.swap(scene);

		return scene;
	}

	showSettingsScene(source) {
		const scene = new SettingsScene(source);

		scene.signals.next.add(() => {
			P3.scene.remove();
		});
		scene.signals.home.add(() => {
			this.showSplashScene();
		});
		scene.signals.restart.add(() => {
			this.showGameScene();
		});

		P3.scene.add(scene);

		return scene;
	}

	fadeTransition(a, b, scope) {
		scope = scope || window;

		const transition = new P3.FadeTransition(0x0);

		common.transitions.addChild(transition);

		transition.signals.in.add(() => {
			transition.out();
			a && a.call(scope);
		}, this);
		transition.signals.out.add(() => {
			transition.destroy();
			b && b.call(scope);
		}, this);

		transition.in();
	}
}
